import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import TopNav from "@/components/TopNav";
import Dashboard from "@/pages/Dashboard";
import LeadsList from "@/pages/LeadsList";
import TeamPage from "@/pages/TeamPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ProfilePage from "@/pages/ProfilePage";
import AuditLogPage from "@/pages/AuditLogPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import { Loader2 } from "lucide-react";

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D0D5B]" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <>
      <TopNav />
      <main className="pt-16 pb-20 md:pb-0">
        <Outlet />
      </main>
    </>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D0D5B]" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RoleGuard({ allowedRoles, children }) {
  const { user } = useAuth();
  if (!user || !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F8F9FC]">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

            {/* Protected routes */}
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/leads" element={<LeadsList />} />
              <Route path="/leads/:id" element={<LeadDetailPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/integrations" element={<RoleGuard allowedRoles={["super_admin", "marketing"]}><IntegrationsPage /></RoleGuard>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
